import { Box, Button, RadioGroup, Typography } from "@mui/material";
import {
  FileText,
  Wrench,
  Package,
  ShieldAlert,
  ListOrdered,
  CircleCheck,
} from "lucide-mui";
import { useTheme } from "../context/ThemeContext";
import { FormSelect, RadioField } from "./ui";
import { styledComponent } from "../utils/styled";

const CATEGORY_OPTIONS = ["Automotive", "Aviation", "Maritime", "Other"];
const CATEGORY_SELECT_SX = { mb: 0, width: "223px" };

const SECTIONS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "tools", label: "Required Tools", icon: Wrench },
  { id: "parts", label: "Parts and Assembly", icon: Package },
  { id: "safety", label: "Safety Precautions", icon: ShieldAlert },
  { id: "steps", label: "Steps", icon: ListOrdered },
];

// ── Styled components ─────────────────────────────────────────────────────────

const PanelBodyShell = styledComponent(Box)({
  marginTop: "25px",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
});

const PanelBody = styledComponent(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "13px",
  flex: 1,
  overflow: "scroll",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

const PanelCard = styledComponent(Box)(({ isDark }) => ({
  border: "1px solid",
  borderColor: isDark ? "rgba(255,255,255,0.07)" : "#D5D9DF",
  borderRadius: "12px",
  backgroundColor: isDark ? "rgba(22,25,26,0.8)" : "#FFFFFF",
}));

const PanelHeaderRow = styledComponent(Box)(({ isDark }) => ({
  padding: "16px 20px 17px",
  borderBottom: "1px solid",
  borderColor: isDark ? "rgba(255,255,255,0.06)" : "#D5D9DF",
}));

const PanelHeaderLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 500,
  color: isDark ? "#D5D9DF" : "#434950",
}));

const ChecklistRow = styledComponent(Box)(({ isDark, isLast }) => ({
  display: "flex",
  alignItems: "center",
  gap: "11px",
  padding: "13px 20px",
  borderBottom: isLast ? "none" : "1px solid",
  borderColor: isDark ? "rgba(255,255,255,0.05)" : "#D5D9DF",
  // Direct-child only — a descendant selector would also hit the badge's own icon.
  "& > svg": {
    width: "16px",
    height: "16px",
    color: isDark ? "#D5D9DF" : "#434950",
  },
}));

const ChecklistRowLabel = styledComponent(Typography)(({ isDark }) => ({
  flex: 1,
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 400,
  color: isDark ? "#D5D9DF" : "#434950",
}));

const CompleteBadge = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  gap: "2px",
  padding: "2px 8px",
  borderRadius: "100px",
  backgroundColor: isDark ? "rgba(71,213,166,0.1)" : "rgba(36,161,72,0.1)",
  border: "1px solid",
  borderColor: isDark ? "rgba(71,213,166,0.2)" : "rgba(36,161,72,0.2)",
  "& svg": {
    width: "14px",
    height: "14px",
    color: isDark ? "#47D5A6" : "#24A148",
  },
}));

const CompleteBadgeLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontSize: "12px",
  fontWeight: 500,
  color: isDark ? "#47D5A6" : "#24A148",
}));

const CategoryRow = styledComponent(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
});

const ReadOnlyValue = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontSize: "14px",
  color: isDark ? "#A8AEB6" : "#6A7079",
}));

const SignoffBody = styledComponent(Box)({
  padding: "12px 20px 16px",
});

const SignoffRadioGroup = styledComponent(RadioGroup)({
  gap: "10px",
});

export const TurnOffBtn = styledComponent(Button)({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  color: "#FFFFFF",
  background:
    "linear-gradient(159deg, rgb(184,32,32) 2.8%, rgb(236,83,83) 107%)",
  borderRadius: "10px",
  textTransform: "none",
  height: "36px",
  padding: "0 16px",
  minWidth: 0,
  "&:hover": { opacity: 0.85 },
  "&.Mui-disabled": { opacity: 0.5, color: "#FFFFFF" },
});

// No completeness requirements exist yet — every section always shows Complete.
function SectionChecklistCard({ isDark }) {
  return (
    <PanelCard isDark={isDark}>
      <PanelHeaderRow isDark={isDark}>
        <PanelHeaderLabel isDark={isDark}>Section Review</PanelHeaderLabel>
      </PanelHeaderRow>
      {SECTIONS.map((section, i) => {
        const Icon = section.icon;
        return (
          <ChecklistRow
            key={section.id}
            isDark={isDark}
            isLast={i === SECTIONS.length - 1}
          >
            <Icon />
            <ChecklistRowLabel isDark={isDark}>
              {section.label}
            </ChecklistRowLabel>
            <CompleteBadge isDark={isDark}>
              <CircleCheck />
              <CompleteBadgeLabel isDark={isDark}>Complete</CompleteBadgeLabel>
            </CompleteBadge>
          </ChecklistRow>
        );
      })}
    </PanelCard>
  );
}

export function ReviewPublishPanel({
  dpmStatus,
  category,
  signoffRequirements,
  signoffRequirementsOptions = [],
  selectedCategory,
  onCategoryChange,
  selectedSignoff,
  onSignoffChange,
}) {
  const { isDark } = useTheme();
  const isDraft = dpmStatus === "DRAFT";

  const signoffLabel =
    signoffRequirementsOptions.find(opt => opt.value === signoffRequirements)
      ?.label ?? "Not set";

  return (
    <PanelBodyShell>
      <PanelBody>
        <SectionChecklistCard isDark={isDark} />

        <PanelCard isDark={isDark}>
          <CategoryRow>
            <PanelHeaderLabel isDark={isDark}>Category</PanelHeaderLabel>
            {isDraft ? (
              <FormSelect
                value={selectedCategory}
                onChange={e => onCategoryChange(e.target.value)}
                options={CATEGORY_OPTIONS}
                isDark={isDark}
                containerSx={CATEGORY_SELECT_SX}
              />
            ) : (
              <ReadOnlyValue isDark={isDark}>
                {category ?? "Other"}
              </ReadOnlyValue>
            )}
          </CategoryRow>
        </PanelCard>

        <PanelCard isDark={isDark}>
          <PanelHeaderRow isDark={isDark}>
            <PanelHeaderLabel isDark={isDark}>
              Sign-off Requirements
            </PanelHeaderLabel>
          </PanelHeaderRow>
          <SignoffBody>
            {isDraft ? (
              <SignoffRadioGroup
                value={selectedSignoff}
                onChange={e => onSignoffChange(e.target.value)}
              >
                {signoffRequirementsOptions.map(opt => (
                  <RadioField
                    key={opt.value}
                    value={opt.value}
                    label={opt.label}
                    isDark={isDark}
                  />
                ))}
              </SignoffRadioGroup>
            ) : (
              <ReadOnlyValue isDark={isDark}>{signoffLabel}</ReadOnlyValue>
            )}
          </SignoffBody>
        </PanelCard>
      </PanelBody>
    </PanelBodyShell>
  );
}
